import { h } from 'preact'
import {
  sendMultiframeSelfie,
  UploadDocumentPayload,
  uploadFacePhoto,
  uploadFaceVideo,
  UploadVideoPayload,
} from '~utils/onfidoApi'
import { CapturePayload, FaceCapture } from '~types/redux'

import {
  CallbackNames,
  CALLBACK_TYPES,
  ConfirmProps,
  OnApiErrorCallback,
  OnApiSuccessCallback,
  OnSubmitCallback,
  useConfirm,
} from './useConfirm'
import Spinner from 'components/Spinner'
import Previews from './Previews'

export type SelfieConfirmProps = Omit<
  ConfirmProps,
  'upload' | 'submitUpload' | 'method'
>

export const SelfieConfirm = (props: SelfieConfirmProps) => {
  const handleSelfieUpload = (
    { snapshot, ...selfie }: FaceCapture,
    token: string,
    onApiSuccess: OnApiSuccessCallback,
    onApiError: OnApiErrorCallback
  ) => {
    const url = props.urls.onfido_api_url
    // if snapshot is present, it needs to be uploaded together with the user initiated selfie
    if (snapshot) {
      sendMultiframeSelfie(
        snapshot,
        selfie,
        token,
        url,
        onApiSuccess,
        onApiError
      )
    } else {
      const { blob, filename, sdkMetadata } = selfie
      // filename is only present for images taken via webcam.
      // Captures that have been taken via the Uploader component do not have filename
      // and the blob is a File type
      const filePayload = filename ? { blob, filename } : blob
      uploadFacePhoto(
        { file: filePayload, sdkMetadata },
        url,
        token,
        onApiSuccess,
        onApiError
      )
    }
  }

  const uploadCaptureToOnfido = (
    capture: CapturePayload,
    onApiSuccess: OnApiSuccessCallback,
    onApiError: OnApiErrorCallback,
    onSubmitCallback: OnSubmitCallback
  ) => {
    const { token, language, isDecoupledFromAPI, urls } = props
    if (!token) {
      throw new Error('token not provided')
    }
    const url = urls.onfido_api_url
    const { blob, variant, challengeData, sdkMetadata } = capture

    if (variant === 'video') {
      const data: UploadVideoPayload = {
        challengeData,
        blob,
        language,
        sdkMetadata,
      }
      if (isDecoupledFromAPI) onSubmitCallback(data, CALLBACK_TYPES.video)
      else uploadFaceVideo(data, url, token, onApiSuccess, onApiError)
    } else if (isDecoupledFromAPI) {
      onSubmitCallback(capture, CALLBACK_TYPES.selfie)
    } else
      handleSelfieUpload(
        capture as FaceCapture,
        token,
        onApiSuccess,
        onApiError
      )
  }

  const submitUpload = (
    data: FaceCapture | UploadVideoPayload | UploadDocumentPayload,
    callbackName: CallbackNames,
    url: string | undefined,
    token: string,
    onApiSuccess: OnApiSuccessCallback,
    onApiError: OnApiErrorCallback
  ) => {
    if (callbackName === CALLBACK_TYPES.video) {
      uploadFaceVideo(
        data as UploadVideoPayload,
        url,
        token,
        onApiSuccess,
        onApiError
      )
      return
    }

    if (callbackName === CALLBACK_TYPES.selfie) {
      handleSelfieUpload(data as FaceCapture, token, onApiSuccess, onApiError)
      return
    }
  }

  const {
    actions,
    capture,
    completeStep,
    crossDeviceClientError,
    enterpriseFeatures,
    nextStep,
    previousStep,
    resetSdkFocus,
    token,
    trackScreen,
    triggerOnError,
    urls,
  } = props

  const {
    uploadInProgress,
    confirmError,
    onConfirm,
    onRetake,
    onVideoPreviewError,
  } = useConfirm(
    actions,
    capture,
    'face',
    token,
    urls,
    completeStep,
    nextStep,
    previousStep,
    resetSdkFocus,
    submitUpload,
    trackScreen,
    triggerOnError,
    uploadCaptureToOnfido,
    crossDeviceClientError,
    enterpriseFeatures
  )

  if (uploadInProgress) {
    return <Spinner />
  }

  return (
    <Previews
      isFullScreen={!!props.isFullScreen}
      capture={props.capture}
      retakeAction={onRetake}
      confirmAction={onConfirm}
      isUploading={uploadInProgress}
      error={confirmError}
      trackScreen={props.trackScreen}
      method={'face'}
      documentType={props.documentType}
      forceRetake={confirmError?.type === 'error'}
      onVideoError={onVideoPreviewError}
    />
  )
}

export default SelfieConfirm
